<template>
  <div class="login-wrapper">
    <div class="login-container">
      <!-- Logo -->
      <img
        :src="'/static/icons/BaanRueJai.svg'"
        class="logo"
        alt="BaanRueJai Logo"
      />

      <!-- Employee Section -->
      <section class="mb-5">
        <h2 class="section-title ms-title">สำหรับพนักงาน</h2>
        <sc-button variant="white" class="w-100" @click="msLogin">
          <img
            src="/static/icons/microsoft-icon.svg"
            class="me-2"
            alt="Microsoft Icon"
          />
          ลงชื่อเข้าใช้ด้วย Microsoft
        </sc-button>
      </section>

      <el-divider class="section-divider mb-5">หรือ</el-divider>

      <!-- Partner Section -->
      <section>
        <h2 class="section-title partner-title">สำหรับพาร์ทเนอร์</h2>

        <el-input
          v-model="email"
          v-validate="'required|email'"
          :placeholder="$t('feature.login.email')"
          clearable
          :disabled="isLoading"
          :class="{ error: errors.has('email') || error }"
          name="email"
          autocomplete="username"
          class="mb-3"
        ></el-input>

        <el-input
          v-model="password"
          v-validate="'required|min:6'"
          :placeholder="$t('feature.login.password')"
          :class="{ error: errors.has('password') || error }"
          name="password"
          autocomplete="current-password"
          clearable
          show-password
          :disabled="isLoading"
          class="mb-2"
        ></el-input>

        <div v-if="combinedError" class="alert-error">
          <i class="fa fa-info-circle"></i>
          {{ combinedError }}
        </div>

        <sc-button
          variant="white-orange"
          class="partner-login-btn"
          :disabled="isLoading"
          @click="onSubmit"
        >
          <span v-if="isLoading" class="btn-spinner"></span>
          {{ isLoading ? $t("feature.login.loading") : $t("feature.login.label") }}
        </sc-button>
      </section>
    </div>

    <!-- Version info fixed bottom right -->
    <div class="version-info">
      <small>{{ $t("version") }} {{ version }}</small>
    </div>
  </div>
</template>

<script>
import ScButton from "@/lib/components/ScButton"
import packageJson from "@/../package.json"
import { client } from "@/god"
import { baanRueJaiClient, backendAdminClient } from "@/dependencies"
import { setCookie, unsetCookie, Auth } from "@/lib/cookie"

export default {
  name: "AdminLoginPage",
  components: {
    ScButton,
  },
  data() {
    return {
      email: "",
      password: "",
      error: false,
      isLoading: false,
      version: packageJson.version,
      errorMessage: "",
    }
  },
  computed: {
    combinedError() {
      // If both email and password are empty, no error to show
      if (!this.email && !this.password) {
        return null
      }

      if (this.errorMessage) {
        return this.errorMessage
      }

      const emailError = this.errors.first("email")
      const passwordError = this.errors.first("password")

      if (emailError) return emailError
      if (passwordError) return passwordError

      return this.error ? this.$t("feature.login.invalid") : null
    },
  },
  watch: {
    email() {
      this.clearErrorMessage()
    },
    password() {
      this.clearErrorMessage()
    },
  },
  methods: {
    onSubmit() {
      return this.$validator.validateAll().then((result) => {
        if (result) {
          unsetCookie(Auth.AccessToken)
          unsetCookie(Auth.RefreshToken)

          if (this.isEmployeeEmail(this.email)) {
            this.errorMessage = this.$t("feature.login.login_sso_only");
            return;
          }

          this.isLoading = true;

          client
            .post(
              "/admin/auth/login",
              {
                email: this.email,
                password: this.password,
              },
              {
                headers: { "X-Frontend-Source": "sc-frontend" },
              },
            )
            .then(
              (response) => {
                const { accessToken, refreshToken } = response.data

                try {
                  if (this.isLoggedInFromRjServices()) {
                    this.broadcastCredentials(refreshToken, accessToken)
                  }
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.debug(error)
                }

                baanRueJaiClient.setAuthorizationToken(accessToken)
                setCookie(Auth.AccessToken, accessToken)
                setCookie(Auth.RefreshToken, response.data.refreshToken)

                // client's request interceptor attaches the Bearer token
                // (just stored in the cookie above) automatically.
                return client.get("/admin/auth/").then((res) => {
                  localStorage.setItem("adminFirstName", res.data.firstName)
                  localStorage.setItem("adminLastName", res.data.lastName)
                  localStorage.setItem("adminRole", res.data.role)
                  localStorage.setItem(
                    "adminRolePermission",
                    JSON.stringify(res.data.adminRole),
                  )
                  localStorage.setItem("adminRoleId", res.data.adminRole.roleId)
                  localStorage.setItem("adminId", res.data.adminId)
                  localStorage.setItem(
                    "adminProjects",
                    res.data.adminProjects
                      .map((project) => project.projectId)
                      .join(),
                  )
                  if (this.$route.query.redirect !== undefined) {
                    this.$router.push({ path: this.$route.query.redirect })
                  } else {
                    this.setDefaultRouteByPermission(res.data.adminRole)
                  }
                })
              },
              (error) => {
                this.isLoading = false;

                const errorCode =
                  error.response &&
                  error.response.data &&
                  error.response.data.errorCode

                if (errorCode === "EMPLOYEE_USE_SSO_ONLY") {
                  this.errorMessage = this.$t("feature.login.login_sso_only")
                } else {
                  this.errorMessage = this.$t("feature.login.invalid")
                }
              },
            )
            .catch((e) => {
              this.isLoading = false;
              this.errorMessage = this.getRequestErrorMessage(e);
            });
        }
      })
    },
    getRequestErrorMessage(error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        (error.body && error.body.message) ||
        (error.data && error.data.message) ||
        error.message

      return message || this.$t("feature.login.error")
    },
    isLoggedInFromRjServices() {
      // Check if rj services such as RJ Staff App is using this login page
      return (
        window.authJsHandler &&
        typeof window.authJsHandler.postMessage === "function"
      )
    },
    broadcastCredentials(refreshToken, accessToken) {
      if (!refreshToken || !accessToken) {
        // eslint-disable-next-line no-console
        console.error("Refresh token or access token is empty")
        return
      }

      if (!this.isLoggedInFromRjServices()) {
        // eslint-disable-next-line no-console
        console.error("Only allowed to broadcast credentials to rj services")
        return
      }

      window.authJsHandler.postMessage(
        JSON.stringify({
          refresh_token: refreshToken,
          access_token: accessToken,
        }),
      )
    },
    setDefaultRouteByPermission(permission = null) {
      if (permission && permission.features) {
        const firstFeature = permission.features[0]
        switch (firstFeature.featureId) {
          case 1:
            this.$router.push({
              name: "ArticleList",
              params: { articleType: "news" },
            })
            break
          case 2:
            this.$router.push({
              name: "ArticleList",
              params: { articleType: "activities" },
            })
            break
          case 3:
            this.$router.push({
              name: "ArticleList",
              params: { articleType: "privileges" },
            })
            break
          case 4:
            this.$router.push({ name: "ContactProject" })
            break
          default:
            this.$router.push({ name: "Dashboard" })
        }
      } else {
        this.$router.push({ name: "Dashboard" })
      }
    },
    msLogin() {
      backendAdminClient.auth.microsoft.login()
    },
    isEmployeeEmail(email) {
      // Environment Vars
      const { NODE_ENV } = process.env

      // Validate Environment
      if (NODE_ENV === "local") return false

      // Validate Email
      if (!email) return false

      const lowerEmail = email.toLowerCase()

      return (
        lowerEmail.endsWith("@scasset.com") ||
        lowerEmail.endsWith("@scasble.com")
      )
    },
    clearErrorMessage() {
      if (this.errorMessage) {
        this.errorMessage = ""
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.login-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 24px;
  background-color: white;
  overflow: hidden;
}

.login-container {
  width: 100%;
  max-width: 320px;
  border-radius: 8px;
  z-index: 1;
  box-sizing: border-box;
  color: #333;
}

.logo {
  margin: 0 auto 48px;
  max-width: 180px;
}

.section-title {
  font-weight: 600;
  margin-bottom: 16px;
  text-align: left;
}
.ms-title {
  font-size: 1.3rem;
}
.partner-title {
  font-size: 1rem;
}

.section-divider {
  border-color: rgba(95, 95, 95, 0.8);
}

.alert-error {
  color: red;
  font-size: 13px;
  text-align: left;
  margin-bottom: 8px;
}

.btn-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 6px;
  vertical-align: middle;
  border: 2px solid rgba(255, 122, 0, 0.3);
  border-top-color: #ff7a00;
  border-radius: 50%;
  animation: btn-spin 0.7s linear infinite;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

.partner-login-btn {
  display: inline-block;
  padding: 8px 24px;
  border: 1.5px solid #ff7a00 !important;
  color: #ff7a00 !important;
  background: transparent !important;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  margin-top: 12px;
  border-radius: 4px;
}
.partner-login-btn:hover,
.partner-login-btn:focus {
  background-color: rgba(255, 122, 0, 0.15) !important;
  color: #ff7a00 !important;
  border-color: #ff7a00 !important;
}

.version-info {
  position: fixed;
  bottom: 16px;
  right: 16px;
  color: #333;
  font-size: 0.9rem;
}

.el-input {
  width: 100%;
}

@media (max-width: 480px) {
  .login-container {
    max-width: 100%;
    padding: 16px;
  }
}
</style>
